import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from '@/i18n';

import type { TableColumn, TableAction } from '@/components/ui/tables/Table';
import { progressivityService, type Progressivity } from '@/api/domains/attendance';
import { useUI } from '@/hooks/ui/useUI';
import { colors } from '@/assets/styles/colors';
import { usePagePermissions } from '@/hooks/authorization/usePagePermissions';


export interface UseProgressivityListResult {
  rows: Progressivity[];
  columns: TableColumn<Progressivity>[];
  actions: TableAction<Progressivity>[];
  loading: boolean;
  refetch: () => Promise<void>;
  handleCreate: () => void;
}

export function useProgressivityList(): UseProgressivityListResult {
  const { t: translate } = useTranslation();
  const { showNotification, showApiError, openSidePanel, openModal } = useUI();
  const { canEdit, canDelete } = usePagePermissions();

  const [rows, setRows] = useState<Progressivity[]>([]);
  const [loading, setLoading] = useState(false);
  const latestRequestIdRef = useRef(0);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const requestId = ++latestRequestIdRef.current;

    const response = await progressivityService.getProgressivities();

    if (requestId !== latestRequestIdRef.current) return;

    if (!response.success || !response.data) {
      showApiError(response, translate('configuration.workday.loadError'));
      setRows([]);
      setLoading(false);
      return;
    }

    setRows(response.data);
    setLoading(false);
  }, [showApiError, translate]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const handleCreate = useCallback(() => {
    openSidePanel({
      title: translate('attendance:configuration.workday.panelTitle.create'),
      component: 'ProgressivityFormPanel',
      props: { onSuccess: fetchData },
    });
  }, [openSidePanel, translate, fetchData]);

  const columns: TableColumn<Progressivity>[] = useMemo(() => [
    {
      key: 'startDate',
      header: translate('attendance:configuration.workday.columns.effectiveDate'),
      sortable: false,
      render: (row: Progressivity) => row.startDate,
    },
    {
      key: 'weeklyHours',
      header: translate('attendance:configuration.workday.columns.weeklyHours'),
      sortable: false,
      render: (row: Progressivity) => row.weeklyHours,
    },
  ], [translate]);

  const actions: TableAction<Progressivity>[] = useMemo(() => [
    {
      id: 'edit',
      label: translate('attendance:configuration.workday.actions.edit'),
      icon: 'edit',
      color: colors.important.main,
      hidden: () => !canEdit,
      onClick: (row: Progressivity) => {
        openSidePanel({
          title: translate('attendance:configuration.workday.panelTitle.edit'),
          component: 'ProgressivityFormPanel',
          props: { record: row, onSuccess: fetchData },
        });
      },
    },
    {
      id: 'delete',
      label: translate('attendance:configuration.workday.actions.delete'),
      icon: 'close',
      color: colors.feedback.error300,
      hidden: () => !canDelete,
      onClick: (row: Progressivity) => {
        openModal('ConfirmModal', {
          variant: 'error',
          icon: 'alert',
          title: translate('attendance:configuration.workday.messages.deleteTitle'),
          bodyText: translate('attendance:configuration.workday.messages.deleteBody'),
          onConfirm: async () => {
            const response = await progressivityService.deleteProgressivity(row.rawStartDate);
            if (response.success) {
              showNotification('success', translate('attendance:configuration.workday.messages.deleteSuccess'), 3000);
              void fetchData();
            } else {
              showApiError(response, translate('attendance:configuration.workday.loadError'));
            }
          },
        });
      },
    },
  ], [
    translate, openSidePanel, openModal,
    showApiError, showNotification, fetchData, canEdit, canDelete,
  ]);

  return {
    rows,
    columns,
    actions,
    loading,
    refetch: fetchData,
    handleCreate,
  };
}

export default useProgressivityList;

